
























































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Mutation, Getter } from 'vuex-class';
import { DatePicker, FluentDate } from '@/features/reports/services';
import { SET_REPORTING_DATE_RANGE } from '@/store/reports/mutationTypes';
import { DateGrouping, DateOption } from '@/features/reports/models';
import moment from 'moment';
import {mixin as clickaway} from 'vue-clickaway';

@Component({
	mixins: [
		clickaway
	]
})
export default class DateRangeFilter extends Vue {
	@Mutation(SET_REPORTING_DATE_RANGE) public setReportingDateRange: (dateOption: DateOption) => void;
	@Getter public selectedDateOption: DateOption;
	@Getter public isReportLoading: boolean;

	public dateSelectorOpen: boolean = false;

	public $refs: { calendar: any };

	public value: DateOption = DatePicker.options.today;

	public selectedDateRange = DatePicker.options.today.getDateRange();

	public datePickerModelConfig = {
		start: {
			timeAdjust: '00:00:00'
		},
		end: {
			timeAdjust: '23:59:59'
		}
	};

	public availableDates = {
		start: new FluentDate(new Date(2018, 0, 1)).startOfYear().value,
		end: new FluentDate().endOfYear().value
	};

	public datePickerAttributes = [{
		key: 'today',
		highlight: {
			fillMode: 'outline'
		},
		dates: new Date()
	}];

	public mounted() {
		if (!this.isSelectedDateInOptions(this.value)) {
			const val = this.getSelectedPreset() ?? this.getDateOptionForCustomRange();
			this.setReportingDateRange(val);
		}
	}

	@Watch('selectedDateOption', { immediate: true })
	public onSelectedDateOptionChange() {
		this.value = this.selectedDateOption;
		this.selectedDateRange = this.selectedDateOption.getDateRange();
		this.moveCalendar();
	}

	public handleDateChange(): void {
		const selectedPreset = this.getSelectedPreset();
		if (selectedPreset) {
			this.value = selectedPreset;
			return;
		}

		this.value = this.getDateOptionForCustomRange();
	}

	public isSelectedDateInOptions(value: DateOption) {
		return this.dateOptions.some(option => option.label === value.label);
	}

	public get dateOptions(): DateOption[] {
		return DatePicker.options.toArray();
	}

	public getSelectedPreset(): DateOption | undefined {
		const matchingValues = DatePicker.options.toArray().filter((opt: DateOption) => {
			const optRange = opt.getDateRange();
			return (
				moment(this.selectedDateRange.start).isSame(optRange.start, 'day') &&
				moment(this.selectedDateRange.end).isSame(optRange.end, 'day')
			);
		});
		const matchingValueWithLabel = matchingValues.find((opt: DateOption) => {
			return opt.label === this.value.label;
		});
		return matchingValueWithLabel ? matchingValueWithLabel : matchingValues[0];
	}

	public getDateOptionForCustomRange(): DateOption {
		const selectedStart = this.selectedDateRange.start;
		const selectedEnd = this.selectedDateRange.end;
		return {
			group: this.getGroupingForCustomRange(),
			getDateRange: () => ({
				start: selectedStart,
				end: selectedEnd
			}),
			label: this.generateLabelForCustomRange(),
			listGroup: 0
		};
	}

	public cancel(): void {
		this.value = this.selectedDateOption;
		this.selectedDateRange = this.selectedDateOption.getDateRange();

		this.dateSelectorOpen = false;
	}

	public toggleDateSelector(): void {
		if (this.dateSelectorOpen) {
			this.cancel();
		} else {
			this.dateSelectorOpen = true;
		}
	}

	public isActivePreset(label?: string): boolean {
		if (!label) {
			return !DatePicker.options.toArray().some((opt: DateOption) => opt.label === this.value.label);
		}
		return this.value.label === label;
	}

	public setDateOption(dateRange: DateOption): void {
		if (this.$refs.calendar) {
			this.$refs.calendar.dragValue = null;
		}
		this.value = dateRange;
		this.selectedDateRange = dateRange.getDateRange();
		this.moveCalendar();
	}

	public applySelection(): void {
		this.setReportingDateRange(this.value);
		this.toggleDateSelector();
	}

	public shouldDisplayPresetDivider(index: number): boolean {
		if (index === 0) {
			return false;
		}
		return this.dateOptions[index].listGroup > this.dateOptions[index - 1].listGroup;
	}

	private getDaysBetweenDates(date1: Date, date2: Date): number {
		const oneDay = 24 * 60 * 60 * 1000;
		return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
	}

	private generateLabelForCustomRange(): string {
		if (this.startDateEqualsEndDate()) {
			return moment(this.selectedDateRange.start).utc().format('MMM D, YYYY');
		}
		return `${moment(this.selectedDateRange.start).utc().format('MMM D, YYYY')} - ${moment(this.selectedDateRange.end).utc().format(
			'MMM D, YYYY'
		)}`;
	}

	private startDateEqualsEndDate() {
		return this.getDaysBetweenDates(this.selectedDateRange.start, this.selectedDateRange.end) <= 1;
	}

	private moveCalendar(): void {
		this.selectedDateRange = this.value.getDateRange();
		if (this.$refs.calendar) {
			this.$refs.calendar.move(this.selectedDateRange.start, {
				position: 1,
				transition: 'slide-h'
			});
		}
	}

	private getGroupingForCustomRange(): DateGrouping {
		const daySpan = this.getDaysBetweenDates(this.selectedDateRange.start, this.selectedDateRange.end);
		if (daySpan === 1) {
			return DateGrouping.HOUR;
		}
		return daySpan < 91 ? DateGrouping.DAY : DateGrouping.MONTH;
	}
}
